



























































































































































































































import Vue, { PropType } from "vue";
import { PPOrder } from "@/picking-portal/models/PPOrder.ts";
import { PPOrderStatus } from "@/picking-portal/enums/PPOrderStatus.ts";
import { PPOrderType, PPOrderTypeMap } from "@/picking-portal/enums/PPOrderType";
import { Paths, RouteInfoMap } from "@/common/enums/RouterPaths";
import { ElementColors } from "@/common/theme/ElementColors";
import { DateTime } from "luxon";
import { bookingService } from "@/picking-portal/services/PPBookingService";
import { PPSlotBooking } from "@/picking-portal/models/PPSlotBooking";
import { PPCustomerInfo } from "@/picking-portal/models/PPCustomerInfo";
import { userService } from "@/picking-portal/services/PPUserService";
import { orderService } from "@/picking-portal/services/PPOrderService";
import { authService } from "@/common/services/AuthService";
import { PPPaymentStatus, PPPaymentStatusMap } from "@/picking-portal/enums/PPPaymentStatus.ts";
import PPOrderStatusChip from "@/picking-portal/components/PPOrderStatusChip.vue";
import PPClaimOrder from "@/picking-portal/components/dialogs/PPClaimOrder.vue";
import PPCancelOrder from "@/picking-portal/components/dialogs/PPCancelOrder.vue";
import { PPClickAndCollectStatus } from "@/picking-portal/enums/PPClickAndCollectStatus";
import { storeService } from "@/common/services/StoreService";

export default Vue.extend({
  name: "PPOrderListItem",
  components: { PPCancelOrder, PPOrderStatusChip, PPClaimOrder },
  props: {
    order: { type: Object as PropType<PPOrder>, required: true },
    storeId: { type: String, required: true },
    bookingId: { type: Number, required: true },
  },
  data: () => {
    return {
      PPOrderStatus,
      isLoading: true,
      Colors: ElementColors,
      isUrgent: false,
      OrderStatus: PPOrderStatus,
      slotBooking: {} as PPSlotBooking,
      customerInfo: {} as PPCustomerInfo,
      pickerInfo: {} as PPCustomerInfo,
      restrictedAge: 0 as number,
      showClaimOrder: false,
    };
  },
  computed: {
    isMobile(): boolean {
      return this.$vuetify.breakpoint.mobile;
    },
    isPicker(): boolean {
      return authService.getState()?.userID === this.order.pickerId;
    },
  },
  async beforeMount() {
    this.customerInfo = await userService.getUserInfo(this.order.customerId);
    if (this.order.pickerId) {
      this.pickerInfo = await userService.getUserInfo(this.order.pickerId);
    }
    this.slotBooking = await bookingService.get(this.storeId, this.bookingId);

    if (this.order.status !== PPClickAndCollectStatus.PICKED) {
      const d1 = DateTime.now();
      if (this.slotBooking.start) {
        const d2 = DateTime.fromISO(this.slotBooking.start).minus({ hour: 1 });
        this.isUrgent = d1 > d2;
      }
    }
    (this.order.pickingItems ?? []).forEach((item) => {
      if (item.restrictedAge > this.restrictedAge) {
        this.restrictedAge = item.restrictedAge;
      }
    });
    this.isLoading = false;
  },
  methods: {
    getOrderItemCount(order: PPOrder): number {
      let sum = 0;
      order?.pickingItems?.forEach((item) => (sum += item.expectedQuantity ?? 0));
      return sum;
    },
    getOrderTypeText(type: PPOrderType): string {
      return PPOrderTypeMap.get(type)?.text ?? "modules.packing-portal.order.type.unknown";
    },
    getOrderTypeColor(type: PPOrderType): string {
      return PPOrderTypeMap.get(type)?.color ?? ElementColors.Chip.Default;
    },
    getPaymentStatusText(status: PPPaymentStatus): string {
      return PPPaymentStatusMap.get(status)?.text ?? "modules.packing-portal.payment.status.unknown";
    },
    getPaymentStatusColor(status: PPPaymentStatus): string {
      return PPPaymentStatusMap.get(status)?.color ?? ElementColors.Chip.Default;
    },
    getAgeRestrictionText(): string {
      return `${this.$t("modules.packing-portal.components.order-list-item.age-restriction")} : ${this.restrictedAge}`;
    },
    getDateString(input: string): string {
      return DateTime.fromISO(input, { setZone: true, zone: this.getStoreTimeZone() }).toLocaleString(
        DateTime.DATE_MED,
        { locale: "en-gb" }
      );
    },
    getTimeString(input: string): string {
      return DateTime.fromISO(input, {
        setZone: true,
        zone: this.getStoreTimeZone(),
      }).toLocaleString(DateTime.TIME_SIMPLE, { locale: "en-gb" });
    },
    getPickerText(): string {
      return (
        this.pickerInfo.name ?? this.$t("modules.packing-portal.components.order-list-item.not-assigned").toString()
      );
    },
    async cardClicked(): Promise<void> {
      if (this.order.status === PPClickAndCollectStatus.SUBMITTED) {
        if (!this.order.pickerId) {
          this.onClaimOrder();
        } else if (authService.getState()?.userID !== this.order.pickerId) {
          this.showClaimOrder = true;
        } else {
          await this.goToPickOrder();
        }
      } else if (this.order.status === PPClickAndCollectStatus.PICKED) {
        await this.goToCollectOrder();
      } else if (this.order.status === PPClickAndCollectStatus.COLLECTED) {
        await this.goToCollectOrder();
      } else if (this.order.status === PPClickAndCollectStatus.CANCELLED) {
        await this.goToCollectOrder();
      }
    },
    async assignOrderToPicker(transactionId: string, pickerId: string): Promise<void> {
      await orderService.updateOrderPicker(transactionId, pickerId);
    },
    onClaimOrder(): void {
      this.showClaimOrder = false;
      const pickerId = authService.getState()?.userID;
      if (pickerId) {
        this.assignOrderToPicker(this.order.transactionId, pickerId);
        this.goToPickOrder();
      }
    },
    onCloseClaimOrder(): void {
      this.showClaimOrder = false;
    },
    async goToPickOrder(): Promise<void> {
      const storeId = this.storeId.toString();
      const bookingId = this.order.booking.bookingId.toString();
      const transactionId = this.order.transactionId;
      if (!bookingId) return;
      await this.$router.push({
        name: RouteInfoMap.get(Paths.ClickAndCollectPickOrder)?.name,
        params: {
          storeId,
          bookingId,
          transactionId,
        },
      });
    },
    async goToCollectOrder(): Promise<void> {
      const storeId = this.storeId.toString();
      const bookingId = this.order.booking.bookingId.toString();
      const transactionId = this.order.transactionId;
      if (!bookingId) return;
      await this.$router.push({
        name: RouteInfoMap.get(Paths.ClickAndCollectCollectOrder)?.name,
        params: {
          storeId,
          bookingId,
          transactionId,
        },
      });
    },
    onCancelOrder(): void {
      this.$emit("cancel", this.order.transactionId);
    },
    getStoreTimeZone(): string {
      return storeService.getStoreTimeZone(this.storeId) ?? DateTime.local().zone.name;
    },
  },
});
